import api from './api'
import type {
  PoliticaComissaoRequest,
  PoliticaComissaoResponse,
  PoliticaProdutoRequest,
  PoliticaProdutoResponse,
  PoliticaProdutoVigente,
  PoliticaSpreadRequest,
  PoliticaSpreadResponse,
  PoliticaTributariaRequest,
  PoliticaTributariaResponse,
  TipoPoliticaSimulacao,
} from '@/types/simulacaoComissao'

const BASE_URL = '/api/v1/simulacoes/politicas'

/**
 * Políticas versionadas da simulação (D2/D5) — `/v1/simulacoes/politicas/**`.
 *
 * Dado global da Originei, sem tenant; o que protege é o papel: `GET /produtos/vigentes` e
 * `GET /comissao/vigente` são CONSULTOR+; as listas são FINANCEIRO ou ADMIN (P6); propor produto
 * e comissão é ADMIN, propor spread/tributo é FINANCEIRO ou ADMIN; encerrar é ADMIN.
 * Spread e alíquota trafegam como FRAÇÃO — a tela converte de/para percentual na borda.
 *
 * P6 (AN-PP6 §3.4): `POST` de criação é PROPOSTA — responde 202 com `statusPublicacao =
 * PENDENTE_APROVACAO` e não muda nada no cálculo. Vale depois de `aprovar` por OUTRA pessoa
 * (403 `APROVACAO_PELO_PROPONENTE`; produto/comissão exigem FINANCEIRO, 403 `APROVADOR_SEM_PAPEL`);
 * na aprovação o backend encerra a ativa anterior. Encerrar = `vigenciaFim` hoje (emergência, ADMIN).
 */
export const politicasSimulacaoService = {
  // ── leitura CONSULTOR+ ──
  listarProdutosVigentes: async (): Promise<PoliticaProdutoVigente[]> => {
    const lista = await api.get<PoliticaProdutoVigente[]>(`${BASE_URL}/produtos/vigentes`)
    return (lista as unknown as PoliticaProdutoVigente[]) ?? []
  },

  // ── modelo por produto (ADMIN) ──
  listarProdutos: async (): Promise<PoliticaProdutoResponse[]> => {
    const lista = await api.get<PoliticaProdutoResponse[]>(`${BASE_URL}/produtos`)
    return (lista as unknown as PoliticaProdutoResponse[]) ?? []
  },
  criarProduto: async (request: PoliticaProdutoRequest): Promise<PoliticaProdutoResponse> => {
    return (await api.post<PoliticaProdutoResponse>(`${BASE_URL}/produtos`, request)) as unknown as PoliticaProdutoResponse
  },
  encerrarProduto: async (id: string): Promise<PoliticaProdutoResponse> => {
    return (await api.post<PoliticaProdutoResponse>(`${BASE_URL}/produtos/${id}/encerrar`)) as unknown as PoliticaProdutoResponse
  },

  // ── spread parceiro × produto (ADMIN) ──
  listarSpreads: async (): Promise<PoliticaSpreadResponse[]> => {
    const lista = await api.get<PoliticaSpreadResponse[]>(`${BASE_URL}/spreads`)
    return (lista as unknown as PoliticaSpreadResponse[]) ?? []
  },
  criarSpread: async (request: PoliticaSpreadRequest): Promise<PoliticaSpreadResponse> => {
    return (await api.post<PoliticaSpreadResponse>(`${BASE_URL}/spreads`, request)) as unknown as PoliticaSpreadResponse
  },
  encerrarSpread: async (id: string): Promise<PoliticaSpreadResponse> => {
    return (await api.post<PoliticaSpreadResponse>(`${BASE_URL}/spreads/${id}/encerrar`)) as unknown as PoliticaSpreadResponse
  },

  // ── tributos (ADMIN) ──
  listarTributos: async (): Promise<PoliticaTributariaResponse[]> => {
    const lista = await api.get<PoliticaTributariaResponse[]>(`${BASE_URL}/tributos`)
    return (lista as unknown as PoliticaTributariaResponse[]) ?? []
  },
  criarTributo: async (request: PoliticaTributariaRequest): Promise<PoliticaTributariaResponse> => {
    return (await api.post<PoliticaTributariaResponse>(`${BASE_URL}/tributos`, request)) as unknown as PoliticaTributariaResponse
  },
  encerrarTributo: async (id: string): Promise<PoliticaTributariaResponse> => {
    return (await api.post<PoliticaTributariaResponse>(`${BASE_URL}/tributos/${id}/encerrar`)) as unknown as PoliticaTributariaResponse
  },

  // ── P4 (AN-PP4 §2.5): política de comissão global — contrato do desenho, ajustar na integração com o B1 ──
  /** Histórico de versões (mais recente primeiro), com faixas — ADMIN. */
  listarComissao: async (): Promise<PoliticaComissaoResponse[]> => {
    const lista = await api.get<PoliticaComissaoResponse[]>(`${BASE_URL}/comissao`)
    return (lista as unknown as PoliticaComissaoResponse[]) ?? []
  },
  /**
   * Versão vigente na data (padrão: hoje no servidor) — CONSULTOR+. `null` quando não há
   * versão vigente (404 ou corpo vazio): a simulação calculável fica bloqueada.
   */
  comissaoVigente: async (dataReferencia?: string): Promise<PoliticaComissaoResponse | null> => {
    try {
      const vigente = await api.get<PoliticaComissaoResponse>(`${BASE_URL}/comissao/vigente`, {
        params: dataReferencia ? { dataReferencia } : undefined,
      })
      return (vigente as unknown as PoliticaComissaoResponse) ?? null
    } catch (e) {
      if ((e as { status?: number })?.status === 404) return null
      throw e
    }
  },
  /** PROPÕE versão nova (202, PENDENTE_APROVACAO; a aberta só é encerrada na aprovação) — ADMIN_ONLY. */
  criarComissao: async (request: PoliticaComissaoRequest): Promise<PoliticaComissaoResponse> => {
    return (await api.post<PoliticaComissaoResponse>(`${BASE_URL}/comissao`, request)) as unknown as PoliticaComissaoResponse
  },
  /** `vigenciaFim = hoje`: toda simulação calculável passa a bloquear — ADMIN_ONLY. */
  encerrarComissao: async (id: string): Promise<PoliticaComissaoResponse> => {
    return (await api.post<PoliticaComissaoResponse>(`${BASE_URL}/comissao/${id}/encerrar`)) as unknown as PoliticaComissaoResponse
  },

  // ── P6 (AN-PP6 §3.4): decisão sobre a proposta (FINANCEIRO ou ADMIN, nunca o proponente) ──
  /** `POST /{tipo}/{id}/aprovar` — corpo `{versaoRegistro}` (409 `CONFLITO_VERSAO` se mudou). */
  aprovar: async (tipo: TipoPoliticaSimulacao, id: string, versaoRegistro?: number | null): Promise<unknown> =>
    api.post(`${BASE_URL}/${tipo}/${id}/aprovar`, versaoRegistro != null ? { versaoRegistro } : {}),
  /** `POST /{tipo}/{id}/rejeitar` — motivo obrigatório (422 `MOTIVO_REJEICAO_OBRIGATORIO`). */
  rejeitar: async (
    tipo: TipoPoliticaSimulacao,
    id: string,
    motivo: string,
    versaoRegistro?: number | null
  ): Promise<unknown> =>
    api.post(`${BASE_URL}/${tipo}/${id}/rejeitar`, {
      motivo,
      ...(versaoRegistro != null ? { versaoRegistro } : {}),
    }),
  /**
   * `POST /{tipo}/{id}/retirar` (revisão PP6-valr, 8b): o PRÓPRIO proponente retira a proposta pendente — vira
   * REJEITADA "Retirada pelo proponente" e libera a chave (403 `RETIRADA_SO_PELO_PROPONENTE` para outra pessoa).
   */
  retirar: async (tipo: TipoPoliticaSimulacao, id: string): Promise<unknown> =>
    api.post(`${BASE_URL}/${tipo}/${id}/retirar`, {}),
}
